import { useCallback, useEffect, useState } from 'react';
import type { Cohort } from '@starter/shared';
import { useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

type FormMode = 'create' | 'edit' | null;

export function CohortsPage() {
  const programId = useProgramId();
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<FormMode>(null);
  const [editing, setEditing] = useState<Cohort | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState<Cohort['status']>('planned');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .listCohorts({ programId, page: 1, pageSize: 100 })
      .then((res) => setCohorts(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load cohorts'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  function openCreate() {
    setEditing(null);
    setName('');
    setCode('');
    setStartDate('');
    setEndDate('');
    setStatus('planned');
    setFormError(null);
    setMode('create');
  }

  function openEdit(cohort: Cohort) {
    setEditing(cohort);
    setName(cohort.name);
    setCode(cohort.code);
    setStartDate(cohort.startDate ?? '');
    setEndDate(cohort.endDate ?? '');
    setStatus(cohort.status);
    setFormError(null);
    setMode('edit');
  }

  function closeModal() {
    setMode(null);
    setEditing(null);
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (mode === 'create') {
        await api.createCohort({
          programId,
          name,
          code,
          startDate: startDate || null,
          endDate: endDate || null,
        });
      } else if (mode === 'edit' && editing) {
        await api.updateCohort(editing.id, {
          name,
          status,
          startDate: startDate || null,
          endDate: endDate || null,
        });
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Cohorts"
        subtitle="Manage student cohorts and timelines"
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Add cohort
          </button>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading cohorts…" />
      ) : (
        <div className="card">
          {cohorts.length === 0 ? (
            <p className="text-muted">No cohorts yet. Create one to enroll students.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Dates</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((c) => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>
                        <code>{c.code}</code>
                      </td>
                      <td>
                        <StatusBadge status={c.status} />
                      </td>
                      <td>
                        {c.startDate ?? '—'} → {c.endDate ?? '—'}
                      </td>
                      <td>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => openEdit(c)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={mode !== null}
        title={mode === 'create' ? 'Create cohort' : 'Edit cohort'}
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="cohort-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <form id="cohort-form" onSubmit={handleSubmit}>
          {formError && (
            <div className="banner banner--error" role="alert">
              {formError}
            </div>
          )}
          <label className="field">
            <span className="field__label">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              disabled={mode === 'edit'}
            />
          </label>
          {mode === 'edit' && (
            <label className="field">
              <span className="field__label">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as Cohort['status'])}>
                <option value="planned">planned</option>
                <option value="active">active</option>
                <option value="completed">completed</option>
                <option value="archived">archived</option>
              </select>
            </label>
          )}
          <div className="form-row">
            <label className="field">
              <span className="field__label">Start date</span>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">End date</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
        </form>
      </Modal>
    </div>
  );
}
